import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Clock, Send, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT, SECTORS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_KEYS = ["services.s1t", "services.s2t", "services.s3t"];
const SERVICE_VALUES = ["Investment", "Loans", "Partnership"];

const FacebookIcon = ({ size = 21, strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service: "Investment", sector: "", amount: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("form.required"));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t("form.invalidEmail"));
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, language: lang });
      toast.success(t("form.success"), { duration: 6000 });
      setForm({
        name: "", email: "", phone: "", company: "",
        service: "Investment", sector: "", amount: "", message: "",
      });
    } catch {
      toast.error(t("form.error"), { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: MessageCircle,
      label: t("contact.whatsapp"),
      value: CONTACT.whatsappNumber,
      cta: t("contact.waCta"),
      href: CONTACT.whatsappLink,
      testid: "contact-whatsapp-link",
    },
    {
      icon: FacebookIcon,
      label: "Facebook",
      value: "Pacific Investment Group",
      cta: "",
      href: CONTACT.facebook,
      testid: "contact-facebook-link",
    },
    {
      icon: Mail,
      label: t("contact.email"),
      value: CONTACT.email,
      cta: t("contact.emCta"),
      href: `mailto:${CONTACT.email}`,
      testid: "contact-email-link",
    },
  ];

  const inputCls = "input-navy w-full rounded-xl px-4 py-3 text-sm";
  const labelCls = "block text-xs font-medium text-slate-600 mb-2 tracking-wide uppercase";

  return (
    <section id="contact" className="relative py-24 lg:py-32 border-t border-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="flex items-center gap-3 font-mono-brand text-xs tracking-[0.28em] uppercase text-gold-deep mb-5">
              <span className="h-px w-10 bg-gold/70" />
              {t("contact.eyebrow")}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-navy-900">
              {t("contact.title")}
            </h2>

            <div className="mt-10 space-y-4">
              {contactCards.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={c.testid}
                  className="flex items-center gap-5 rounded-2xl card-navy card-navy-hover p-5 group"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/12 text-gold-deep border border-gold/25 shrink-0 group-hover:bg-gold/20 transition-colors duration-400">
                    <c.icon size={21} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-slate-500">{c.label}</p>
                    <p className="text-navy-900 font-medium truncate">{c.value}</p>
                    <p className="text-xs text-gold-deep mt-0.5">{c.cta}</p>
                  </div>
                </a>
              ))}

              <a
                href={CONTACT.directionsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address-link"
                className="flex items-start gap-5 rounded-2xl card-navy card-navy-hover p-5 group"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/12 text-gold-deep border border-gold/25 shrink-0 group-hover:bg-gold/20 transition-colors duration-400">
                  <MapPin size={21} strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">{t("contact.address")}</p>
                  <p className="text-navy-900 text-sm leading-relaxed mt-1">{CONTACT.address}</p>
                  <p className="text-xs text-gold-deep mt-1">{t("contact.dir")}</p>
                </div>
              </a>

              <div className="flex items-center gap-3 pl-2 text-sm text-slate-500">
                <Clock size={15} className="text-gold-deep/60" />
                {t("contact.hours")}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.12 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <form
              data-testid="enquiry-form"
              onSubmit={onSubmit}
              className="card-navy rounded-3xl p-7 sm:p-10"
              noValidate
            >
              <p className="font-mono-brand text-xs tracking-[0.28em] uppercase text-gold-deep mb-3">{t("form.eyebrow")}</p>
              <h3 className="font-display text-2xl sm:text-3xl text-navy-900">{t("form.title")}</h3>
              <p className="mt-2 text-sm text-slate-600">{t("form.sub")}</p>

              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="f-name">{t("form.name")} *</label>
                  <input id="f-name" data-testid="enquiry-form-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Cooper" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-email">{t("form.email")} *</label>
                  <input id="f-email" data-testid="enquiry-form-input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-phone">{t("form.phone")}</label>
                  <input id="f-phone" data-testid="enquiry-form-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-company">{t("form.company")}</label>
                  <input id="f-company" data-testid="enquiry-form-input-company" className={inputCls} value={form.company} onChange={set("company")} />
                </div>
              </div>

              <div className="mt-5">
                <span className={labelCls}>{t("form.service")}</span>
                <div className="flex flex-wrap gap-2.5">
                  {SERVICE_KEYS.map((sKey, i) => (
                    <button
                      key={sKey}
                      type="button"
                      data-testid={`service-option-${SERVICE_VALUES[i].toLowerCase()}`}
                      onClick={() => setForm({ ...form, service: SERVICE_VALUES[i] })}
                      className={`rounded-full px-5 py-2.5 text-sm transition-all duration-300 border ${
                        form.service === SERVICE_VALUES[i]
                          ? "btn-gold border-transparent font-semibold"
                          : "border-gold/25 text-slate-700 hover:border-gold/60 hover:text-gold-deep"
                      }`}
                    >
                      {t(sKey)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="f-sector">{t("form.sector")}</label>
                  <select id="f-sector" data-testid="enquiry-form-input-sector" className={inputCls} value={form.sector} onChange={set("sector")}>
                    <option value="">{t("form.pick")}</option>
                    {SECTORS.map((s) => (
                      <option key={s.key} value={s.key}>{t(`sectors.${s.key}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-amount">{t("form.amount")}</label>
                  <input id="f-amount" data-testid="enquiry-form-input-amount" className={inputCls} value={form.amount} onChange={set("amount")} placeholder={t("form.amountPh")} />
                </div>
              </div>

              <div className="mt-5">
                <label className={labelCls} htmlFor="f-message">{t("form.message")} *</label>
                <textarea id="f-message" data-testid="enquiry-form-input-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder={t("form.messagePh")} />
              </div>

              <button
                type="submit"
                data-testid="enquiry-form-submit"
                disabled={submitting}
                className="btn-gold mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold disabled:opacity-60 disabled:pointer-events-none"
              >
                {submitting ? t("form.submitting") : t("form.submit")}
                <Send size={16} strokeWidth={2} />
              </button>
              <p data-testid="form-privacy-note" className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck size={14} className="text-gold-deep shrink-0" />
                {t("form.safe")}
                <a href="/privacy" data-testid="form-privacy-link" className="text-gold-deep hover:underline ml-1">
                  {t("footer.privacy")}
                </a>
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
